import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('calvi-admin-token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
    }
  }, [token]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      const newToken = response.data.access_token;
      localStorage.setItem('calvi-admin-token', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('calvi-admin-token');
    setToken(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const setupAdmin = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/admin/setup`, { email, password });
      return await login(email, password);
    } catch (err) {
      const message = err.response?.data?.detail || 'Setup failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [login]);

  const checkAdminExists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      return response.data.exists;
    } catch (err) {
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      token,
      isAuthenticated,
      loading,
      error,
      login,
      logout,
      setupAdmin,
      checkAdminExists
    }}>
      {children}
    </AuthContext.Provider>
  );
};
